import type { TextAreaChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Input, TextArea } from "@tql/ui-components-react/kendo/inputs";
import { Hint } from "@tql/ui-components-react/kendo/labels";
import { FormField } from "@tql/ui-components-react/tql/form/beta";
import { useState } from "react";

import type { Stop } from "~/features/ftl/shared/ftl-stop";

import {
	appointmentInformationValidator,
	deliveryPONumberValidator,
	MAX_APPOINTMENT_INFO_LENGTH,
	pickupNumberValidator,
	receiverNameValidator,
	shipperNameValidator,
	streetAddressValidator,
} from "./stop-details-validator/stop-details-validator.ts";

const usaImageSrc =
	"data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEgAAABICAMAAABiM0N1AAAASFBMVEVHcEzu7u6yIzSyIzQ8O26yIzTu7u6yIzSyIzSyIzQ8O248O248O248O248O26yIzTu7u5tbJKenbdIR3fn5+15eJv////CwtLh3403AAAADnRSTlMAYBAgIO+vv2DPYL/P78tfW7cAAAC3SURBVHhe7dK9EoQgDIXRq66irpvE//d/0x3pARmoMjlNuq/IXOhlzPylKI5yIzx8FkrghKnznR+VhrhvASxUHmIHzE/gPIkiV9IaPH++j+OmyJW0AU9gv649duUFH1q3bQ3dnFDsPx6/4EOh/2SHQv/JDoX+kx8q35GA6uxI1O7IdmQ7SuMXNIekEs0hrkRbSP+ObEe2o0GqGNBIFQ3guAIHoO25WN8CQDdxoamDh9FxATdCLWP+KPnfX8FNNiYAAAAASUVORK5CYII=";
const canImageSrc =
	"data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEgAAABICAMAAABiM0N1AAAARVBMVEVHcEzVKx7VKx7VKx7VKx7VKx7VKx7u7u7pycfijYblpaDaUEXs4uHovbrXNyvggHnr1tTbXFLedGzYQzjdaF/msa3jmZN+1M+fAAAABnRSTlMAv+9gIM+kpfmZAAABIUlEQVR4Xu3XyYrDMBBFUTuOX2kePP3/pzZthMBNRCOpVo7vXmdXoDfct6enacQl+idcGqfEvGegBwLm9+m80Avh9SvN6IcwD8MEDgjTMPJA4wAeCN8OWSZod4YHWiFZIAUg9EJWEK0AFiJluiAs22lLDeqBAnK6CyKXoaUPihk62iGviGSGLNFmqyEp90AailSGDC3wJJQXNVB674RAaiWZxQooIEkKKX0kR9ediEYpXwd5lLJVUNhQ6qiBgkO5WAGRWFFqMzUQGV1wfPWJLPiQUw0noj44ounWhMM1bRqvX/xxiHigtRk6gBi93CDlEjUgWqFAZ3uSjWmDchJJpG7I3hWK2LkgyQE9f0i2DzvbhOAaNWwzi2v4cU3R2/b09ANpqV6+4bvIUgAAAABJRU5ErkJggg==";

const DEFAULT_BADGE_STYLE: React.CSSProperties = {
	borderRadius: "9999px", // creates the pill shape.
	border: "2px solid var(--kendo-color-primary, #06c)",
	color: "var(--kendo-color-primary, #06c)",
};

function returnCountryFlagImage(country: string) {
	switch (country) {
		case "USA": {
			return usaImageSrc;
		}
		case "CAN": {
			return canImageSrc;
		}
		default: {
			return "";
		}
	}
}

export function StopDetail({
	stop,
	index,
	badgeClassName,
	stopBadgeLabel,
}: Readonly<{
	stop: Readonly<Stop>;
	index: number;
	badgeClassName?: string;
	stopBadgeLabel?: string;
}>) {
	const [appointmentInformation, setAppointmentInformation] = useState("");

	const badgeStyle = badgeClassName
		? { borderRadius: "9999px" }
		: DEFAULT_BADGE_STYLE;

	return (
		<>
			<div className={"k-d-flex k-justify-content-between"}>
				<h4 className={"k-m-0"}>Stop Details</h4>
				<div className={"k-justify-content-end"}>
					<span
						className={badgeClassName ?? "k-px-2 k-py-0.5"}
						style={badgeStyle}
					>
						{stopBadgeLabel ?? stop.stopType}
					</span>
				</div>
			</div>
			<hr></hr>
			<div className={"k-w-full k-py-4 k-d-flex k-flex-column k-gap-1"}>
				<div className={"k-d-flex k-flex-row k-gap-2 k-align-items-center"}>
					<div>
						<h5 className={"k-mb-1"}>
							{stop.location?.postalCode} - {stop.location?.city},{" "}
							{stop.location?.state}
						</h5>
					</div>
					<div>
						<img
							alt={"Country Flag"}
							className={"k-w-6"}
							src={returnCountryFlagImage(stop.location?.country ?? "")}
						/>
					</div>
				</div>
				<div>
					<FormField
						required
						component={Input}
						defaultValue={""}
						label={`${stop.stopType === "Pickup" ? "Shipper" : "Receiver"} Name`}
						name={`stops[${index}].locationName`}
						validator={
							stop.stopType === "Pickup"
								? shipperNameValidator
								: receiverNameValidator
						}
					/>
				</div>
				<div>
					<FormField
						required
						component={Input}
						defaultValue={""}
						label={"Street Address"}
						name={`stops[${index}].streetAddress`}
						validator={streetAddressValidator}
					/>
				</div>
				<div>
					<FormField
						component={Input}
						defaultValue={""}
						label={
							stop.stopType === "Pickup"
								? "Pickup Number"
								: "Delivery PO Number"
						}
						name={`stops[${index}].referenceNumber`}
						validator={
							stop.stopType === "Pickup"
								? pickupNumberValidator
								: deliveryPONumberValidator
						}
					/>
				</div>
				<div>
					<FormField
						component={TextArea}
						label={"Appointment Information"}
						name={`stops[${index}].appointmentInformation`}
						onChange={(e: TextAreaChangeEvent) =>
							setAppointmentInformation(e.target.value ?? "")
						}
						resizable={"none"}
						rows={4}
						validator={appointmentInformationValidator}
						value={appointmentInformation}
					/>
					<Hint direction={"end"}>
						{appointmentInformation.length}/{MAX_APPOINTMENT_INFO_LENGTH}
					</Hint>
				</div>
			</div>
		</>
	);
}
